import { type ICouponRepository, type IUpdateCouponBoardUseCase, NotFoundError } from '../../../domain/entry.js';

/**
 * Use case for updating coupon board data in the store.
 *
 * @param couponRepository Coupon repository dependency
 * @returns Use case executor for updating the coupon board
 */
export function updateCouponBoardUseCase(couponRepository: ICouponRepository): IUpdateCouponBoardUseCase {
  return {
    execute: async ({
      couponId,
      couponIndex,
      boardIndex,
      payload,
    }): ReturnType<IUpdateCouponBoardUseCase['execute']> => {
      const couponBoardEntity = await couponRepository.getCouponBoard({ couponId, couponIndex, boardIndex });
      const couponEntity = await couponRepository.getCoupon({ couponId, couponIndex });
      const isSingleRow = couponEntity?.model.couponType === 'singlerows';

      if (!couponBoardEntity) {
        throw new NotFoundError(
          `Board not found: couponId: ${couponId}, couponIndex: ${couponIndex}, boardIndex: ${boardIndex}`
        );
      }

      const { outcomes: updateOutcomes, ...updateEntity } = couponBoardEntity.validate(payload, isSingleRow);
      const fieldsToUpdateCount = Object.keys(updateEntity).length;

      couponRepository.updateCouponBoard({ couponId, couponIndex, boardIndex }, (board) => {
        if (updateOutcomes) {
          for (const { event, group, outcomes } of updateOutcomes.values) {
            const userOutcomeGroup = board.userOutcomes[event - 1]?.[group];
            const outcomeGroup = board.outcomes[event - 1]?.[group];
            const distributionOutcomeGroup = board.outcomeDistribution[event - 1]?.[group];

            if (!userOutcomeGroup || !outcomeGroup || !distributionOutcomeGroup) {
              continue;
            }

            if (!updateOutcomes.intention) {
              const updated = couponBoardEntity.calculateUpdatedUserOutcomes(outcomes, {
                eventNumber: event,
                group,
              });

              userOutcomeGroup.length = 0;
              Object.assign(userOutcomeGroup, updated);
            }

            if (updateOutcomes.intention === 'reset') {
              userOutcomeGroup.length = 0;
            }

            outcomeGroup.length = 0;
            distributionOutcomeGroup.length = 0;

            Object.assign(outcomeGroup, outcomes);
            Object.assign(distributionOutcomeGroup, couponBoardEntity.calculateOutcomeDistribution(outcomes));
          }
        }

        Object.assign(board, updateEntity);

        // Calculate quickPickVariant after all outcomes have been updated
        if (updateOutcomes) {
          board.quickPickVariant = couponBoardEntity.calculateQuickPickVariant(board);
        }
      });

      return fieldsToUpdateCount;
    },
  };
}
